/**
 * Steady-state benchmark (bench/README.md procedure): fixed seed, scripted
 * camera orbit, governor active, WARMUP excluded, then MEASURE recorded.
 *
 * Output is one row in the bench/results_template.md format plus the raw
 * numbers. Device, OS, and browser columns are left blank for the person
 * running it: a user-agent string cannot identify them reliably.
 */
import type { TierId } from '../config/tiers';

export interface BenchRunResult {
  version: 1;
  date: string;
  warmupS: number;
  measureS: number;
  backend: string;
  physics: string;
  finalTier: TierId;
  tiersSeen: TierId[];
  debris: number;
  frames: number;
  medianFrameMs: number | null;
  p95FrameMs: number | null;
  governorChanges: number;
  governorChangesPer5Min: number;
  userAgent: string;
  /** Ready to paste into bench/results_template.md. */
  row: string;
}

export class BenchRun {
  private readonly samples: Float32Array;
  private n = 0;
  private startMs = -1;
  private changes = 0;
  private readonly seen = new Set<TierId>();
  done = false;

  constructor(
    readonly warmupS: number,
    readonly measureS: number,
  ) {
    // Up to 240 Hz for the whole measured window.
    this.samples = new Float32Array(Math.ceil(measureS * 240) + 16);
  }

  get measuring(): boolean {
    return this.startMs >= 0 && !this.done;
  }

  /** Feed one frame. Returns true once the run has just finished. */
  frame(
    nowMs: number,
    sinceStartMs: number,
    frameMs: number,
    tier: TierId,
    governorChanged: boolean,
  ): boolean {
    if (this.done) return false;
    if (sinceStartMs < this.warmupS * 1000) return false;
    if (this.startMs < 0) this.startMs = nowMs;
    this.seen.add(tier);
    if (governorChanged) this.changes++;
    if (this.n < this.samples.length) this.samples[this.n++] = frameMs;
    if (nowMs - this.startMs >= this.measureS * 1000) {
      this.done = true;
      return true;
    }
    return false;
  }

  status(sinceStartMs: number): string {
    if (this.done) return 'Benchmark finished.';
    const w = this.warmupS * 1000;
    if (sinceStartMs < w)
      return `Benchmark: warming up (${Math.ceil((w - sinceStartMs) / 1000)} s)…`;
    const left = this.measureS - (performance.now() - this.startMs) / 1000;
    return `Benchmark: measuring, ${Math.max(0, Math.ceil(left))} s left. Don't touch the page.`;
  }

  result(info: {
    backend: string;
    physics: string;
    finalTier: TierId;
    debris: number;
  }): BenchRunResult {
    const sorted = this.samples.subarray(0, this.n).sort();
    const q = (p: number): number | null =>
      this.n === 0 ? null : (sorted[Math.min(this.n - 1, Math.floor(p * this.n))] as number);
    const median = q(0.5);
    const p95 = q(0.95);
    const per5 = this.measureS > 0 ? (this.changes * 300) / this.measureS : 0;
    const date = new Date().toISOString().slice(0, 10);
    const notes =
      this.measureS === 300
        ? `seed fixed, orbit camera, ${this.warmupS} s warm-up, tiers seen ${[...this.seen].join('/')}`
        : `SHORT RUN (${this.measureS} s, not the standard 5 min), tiers seen ${[...this.seen].join('/')}`;
    const f = (v: number | null): string => (v === null ? '' : v.toFixed(2));
    const row = `| ${date} |  |  |  | ${info.backend} | ${info.finalTier} | ${info.debris} | ${f(median)} | ${f(p95)} | ${per5.toFixed(1)} | ${notes} |`;
    return {
      version: 1,
      date: new Date().toISOString(),
      warmupS: this.warmupS,
      measureS: this.measureS,
      backend: info.backend,
      physics: info.physics,
      finalTier: info.finalTier,
      tiersSeen: [...this.seen],
      debris: info.debris,
      frames: this.n,
      medianFrameMs: median,
      p95FrameMs: p95,
      governorChanges: this.changes,
      governorChangesPer5Min: per5,
      userAgent: navigator.userAgent,
      row,
    };
  }
}
